import { useQuery } from '@tanstack/react-query';
import { apiData } from './api';

/**
 * 사진 열람 URL 로딩 (§9.1).
 *
 * 사진 격자가 장당 GET /media/:id/url을 따로 부르면 앨범 한 화면에 수십 번
 * 왕복하게 된다(N+1). 같은 렌더 패스에 마운트된 요청을 잠깐 모아
 * GET /media/urls 한 번으로 합쳐 보낸다.
 * 캐시는 서버가 알려 준 만료(expiresInSeconds) 직전까지 유지해, URL이
 * 바뀌며 브라우저 이미지 캐시까지 무효화되는 일을 줄인다.
 */

export interface MediaUrl {
  url: string;
  mimeType?: string;
  expiresInSeconds?: number;
}

interface BatchEntry {
  id: string;
  resolve: (value: MediaUrl) => void;
  reject: (error: unknown) => void;
}

const BATCH_WINDOW_MS = 15;
const BATCH_LIMIT = 50;

let queue: BatchEntry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

async function flush() {
  const batch = queue;
  queue = [];
  timer = null;

  const ids = [...new Set(batch.map((entry) => entry.id))];
  try {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_LIMIT) chunks.push(ids.slice(i, i + BATCH_LIMIT));
    const responses = await Promise.all(
      chunks.map((chunk) =>
        apiData<{ urls: (MediaUrl & { id: string })[] }>(`/media/urls?ids=${chunk.join(',')}`),
      ),
    );
    const byId = new Map(responses.flatMap((r) => r.urls).map((item) => [item.id, item]));
    for (const entry of batch) {
      const hit = byId.get(entry.id);
      if (hit) entry.resolve(hit);
      else entry.reject(new Error('파일을 찾을 수 없어요.'));
    }
  } catch (error) {
    for (const entry of batch) entry.reject(error);
  }
}

export function fetchMediaUrl(id: string): Promise<MediaUrl> {
  return new Promise((resolve, reject) => {
    queue.push({ id, resolve, reject });
    if (timer === null) timer = setTimeout(() => void flush(), BATCH_WINDOW_MS);
  });
}

/** 열람 URL 쿼리 — 모든 사진·첨부·오디오가 이 훅 하나로 캐시를 공유한다 */
export function useMediaUrl(mediaId: string | null | undefined) {
  return useQuery({
    queryKey: ['media', mediaId],
    queryFn: () => fetchMediaUrl(mediaId as string),
    enabled: Boolean(mediaId),
    // 만료 20%를 남기고 갱신 — 만료된 URL을 화면에 물려주지 않는다
    staleTime: (query) =>
      Math.max(60_000, ((query.state.data?.expiresInSeconds ?? 240) * 1000 * 4) / 5),
  });
}
